/**
 * FakeCursor — Animated pointer that shows the player where to click.
 * Moves smoothly to a target position and plays a click animation
 * (scale punch + expanding ring). Depth 560 to sit above bubbles.
 */
import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { TECH } from '@/ui/TechUI';
import { EventBus } from '@/events/EventBus';
import { CinematicEvents } from './CinematicEvents';
import { GOD } from '@/config/GodMode';

const DEPTH_CURSOR = 560;
const CURSOR_SIZE = 20;

export class FakeCursor {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private arrowGfx: Phaser.GameObjects.Graphics;
  private ringGfx: Phaser.GameObjects.Graphics;
  private moveTween: Phaser.Tweens.Tween | null = null;
  private arrowTween: Phaser.Tweens.Tween | null = null;
  private ringTween: Phaser.Tweens.Tween | null = null;
  private clickTimer: Phaser.Time.TimerEvent | null = null;
  private moveClickTimer: Phaser.Time.TimerEvent | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // ── Arrow cursor graphic ──
    this.arrowGfx = scene.add.graphics();
    this.drawArrow();

    // ── Click ring (initially hidden) ──
    this.ringGfx = scene.add.graphics().setAlpha(0);

    this.container = scene.add.container(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, [
      this.arrowGfx,
      this.ringGfx,
    ]).setDepth(DEPTH_CURSOR).setAlpha(0);
  }

  /** Draw a simple arrow cursor pointing top-left */
  private drawArrow(): void {
    const g = this.arrowGfx;
    g.clear();
    // White filled arrow
    g.fillStyle(0xffffff, 1);
    g.beginPath();
    g.moveTo(0, 0);
    g.lineTo(0, CURSOR_SIZE);
    g.lineTo(CURSOR_SIZE * 0.35, CURSOR_SIZE * 0.7);
    g.lineTo(CURSOR_SIZE * 0.55, CURSOR_SIZE);
    g.lineTo(CURSOR_SIZE * 0.7, CURSOR_SIZE * 0.85);
    g.lineTo(CURSOR_SIZE * 0.45, CURSOR_SIZE * 0.6);
    g.lineTo(CURSOR_SIZE * 0.75, CURSOR_SIZE * 0.55);
    g.closePath();
    g.fill();
    // Dark outline
    g.lineStyle(1.5, 0x000000, 0.8);
    g.beginPath();
    g.moveTo(0, 0);
    g.lineTo(0, CURSOR_SIZE);
    g.lineTo(CURSOR_SIZE * 0.35, CURSOR_SIZE * 0.7);
    g.lineTo(CURSOR_SIZE * 0.55, CURSOR_SIZE);
    g.lineTo(CURSOR_SIZE * 0.7, CURSOR_SIZE * 0.85);
    g.lineTo(CURSOR_SIZE * 0.45, CURSOR_SIZE * 0.6);
    g.lineTo(CURSOR_SIZE * 0.75, CURSOR_SIZE * 0.55);
    g.closePath();
    g.strokePath();
  }

  /** Show the cursor (fade in) */
  show(): void {
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: 200,
    });
  }

  /** Move cursor smoothly to a target position (screen coords) */
  moveTo(target: { x: number; y: number }, durationMs?: number): void {
    this.moveTween?.destroy();
    this.show();

    const dur = durationMs ?? (GOD.cinematicCursorSpeed ?? 400);
    const speedMult = GOD.cinematicSpeedMult ?? 1;

    this.moveTween = this.scene.tweens.add({
      targets: this.container,
      x: target.x,
      y: target.y,
      duration: dur / speedMult,
      ease: 'Sine.easeInOut',
    });
  }

  /** Play click animation at current position, then emit FAKE_CLICK_DONE */
  click(holdMs = 200): void {
    const speedMult = GOD.cinematicSpeedMult ?? 1;

    // Scale punch on arrow
    this.arrowTween?.destroy();
    this.arrowTween = this.scene.tweens.add({
      targets: this.arrowGfx,
      scaleX: 0.8,
      scaleY: 0.8,
      duration: 80 / speedMult,
      yoyo: true,
      ease: 'Sine.easeInOut',
    });

    // Expanding ring
    this.ringGfx.clear();
    this.ringGfx.lineStyle(2, TECH.ACCENT, 1);
    this.ringGfx.strokeCircle(0, 0, 8);
    this.ringGfx.setAlpha(1).setScale(1);

    this.ringTween?.destroy();
    this.ringTween = this.scene.tweens.add({
      targets: this.ringGfx,
      scaleX: 3,
      scaleY: 3,
      alpha: 0,
      duration: 400 / speedMult,
      ease: 'Sine.easeOut',
    });

    this.clickTimer?.destroy();
    this.clickTimer = this.scene.time.delayedCall(holdMs / speedMult, () => {
      this.clickTimer = null;
      EventBus.emit(CinematicEvents.FAKE_CLICK_DONE);
    });
  }

  /** Move to target, then click. Full sequence. */
  moveAndClick(target: { x: number; y: number }, moveDurMs?: number, holdMs = 300): void {
    this.moveTo(target, moveDurMs);
    const speedMult = GOD.cinematicSpeedMult ?? 1;
    const moveTime = (moveDurMs ?? (GOD.cinematicCursorSpeed ?? 400)) / speedMult;
    this.moveClickTimer?.destroy();
    this.moveClickTimer = this.scene.time.delayedCall(moveTime + 100, () => {
      this.moveClickTimer = null;
      this.click(holdMs);
    });
  }

  /** Hide cursor (immediate destroy — scene may be paused right after skip) */
  hide(): void {
    this.moveTween?.destroy();
    this.moveTween = null;
    this.arrowTween?.destroy();
    this.arrowTween = null;
    this.ringTween?.destroy();
    this.ringTween = null;
    this.clickTimer?.destroy();
    this.clickTimer = null;
    this.moveClickTimer?.destroy();
    this.moveClickTimer = null;
    // Reset visuals to prevent frozen mid-animation state
    this.arrowGfx.setScale(1);
    this.ringGfx.setAlpha(0).setScale(1);
    this.container.setAlpha(0);
  }

  destroy(): void {
    this.hide();
    this.container.destroy();
  }
}
